import { ReactNode } from 'react';
import { Box, Typography, Card } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ApartmentIcon from '@mui/icons-material/Apartment';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { colors } from '@src/styles';
import { cardTitleSX, iconTileSX, monoFontFamily, monoLabelSX, profileCardSX, tonePillSX } from '../profileStyles';

export type ProfileInfoCardProps = {
  userId: string;
  role?: string;
  team?: string;
  squads?: string[];
};

type InfoRowProps = {
  icon: ReactNode;
  label: string;
  children: ReactNode;
};

const InfoRow = ({ icon, label, children }: InfoRowProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      paddingY: 1.25,
      borderTop: `1px solid ${colors.brand.line}`,
      '&:first-of-type': { borderTop: 'none', paddingTop: 0 },
    }}
  >
    <Box sx={{ color: colors.brand.textMuted, marginTop: '1px', '& svg': { fontSize: 18 } }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ ...monoLabelSX, marginBottom: 0.5 }}>{label}</Typography>
      {children}
    </Box>
  </Box>
);

const valueSX = {
  fontSize: '14px',
  fontWeight: 500,
  color: colors.primary.white,
} as const;

export const ProfileInfoCard = ({ userId, role, team, squads }: ProfileInfoCardProps) => {
  return (
    <Card
      elevation={0}
      sx={{
        ...profileCardSX,
        height: { xs: 'auto', md: 340 },
        display: 'flex',
        flexDirection: 'column',
        padding: 2.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 2 }}>
        <Box sx={iconTileSX}>
          <AccountCircleOutlinedIcon />
        </Box>
        <Typography component="h2" sx={cardTitleSX}>
          Perfil
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
        <InfoRow icon={<BadgeOutlinedIcon />} label="ID do Usuário">
          <Typography
            title={userId}
            sx={{
              ...valueSX,
              fontFamily: monoFontFamily,
              fontSize: '12.5px',
              color: colors.brand.textStrong,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {userId || '-'}
          </Typography>
        </InfoRow>

        <InfoRow icon={<PersonOutlineIcon />} label="Cargo">
          <Typography sx={valueSX}>{role || '-'}</Typography>
        </InfoRow>

        {team && (
          <InfoRow icon={<ApartmentIcon />} label="Torre">
            <Typography sx={valueSX}>{team}</Typography>
          </InfoRow>
        )}

        {squads && squads.length > 0 && (
          <InfoRow icon={<GroupsOutlinedIcon />} label="Times">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {squads.map((squad) => (
                <Box key={squad} sx={tonePillSX(colors.brand.textStrong)}>
                  {squad}
                </Box>
              ))}
            </Box>
          </InfoRow>
        )}
      </Box>
    </Card>
  );
};
